import React, { Fragment, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { usePopperTooltip } from 'react-popper-tooltip';

import ContentTooltip from 'CommonComponents/ContentTooltip';
import TableTooltip from 'CommonComponents/TableTooltip';
import TextTooltip from 'CommonComponents/TextTooltip';

export default function Tooltip({ children, parentEl, allowTooltip = true, triggerRef, ...props }) {
    const { getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
        trigger: props.trigger || 'hover',
        placement: props.placement || 'auto',
        interactive: props.interactive || true,
        offset: props.offset || [0, 6],
        // Controlled visibility (opt-in): lets a click-triggered menu drive open/
        // close state and dismiss after an action. Defaults keep hover tooltips
        // uncontrolled exactly as before.
        ...(props.visible !== undefined ? { visible: props.visible } : {}),
        ...(props.onVisibleChange ? { onVisibleChange: props.onVisibleChange } : {}),
        ...(props.closeOnOutsideClick !== undefined ? { closeOnOutsideClick: props.closeOnOutsideClick } : {}),
        // Gives the pointer time to cross the offset gap into an interactive
        // popover before leaving the trigger closes it.
        ...(props.delayHide !== undefined ? { delayHide: props.delayHide } : {}),
    });

    // The trigger's own ref is replaced by the popper's, so a caller that needs
    // the trigger element passes `triggerRef` and receives it too. Memoised:
    // a fresh callback each render would re-run the popper's ref setter.
    const composedTriggerRef = useCallback(
        (element) => {
            setTriggerRef(element);

            if (typeof triggerRef === 'function') {
                triggerRef(element);
            } else if (triggerRef) {
                triggerRef.current = element;
            }
        },
        [setTriggerRef, triggerRef],
    );

    const component = (
        <Fragment>
            {allowTooltip && props.tooltipType == 'text' && (
                <TextTooltip ref={setTooltipRef} {...getTooltipProps()} backgroundClass={props.backgroundClass} visible={visible}>
                    {props.tooltipContent}
                </TextTooltip>
            )}
            {allowTooltip && props.tooltipType == 'table' && (
                <TableTooltip ref={setTooltipRef} {...getTooltipProps()} visible={visible}>
                    {props.tooltipContent}
                </TableTooltip>
            )}
            {allowTooltip && props.tooltipType == 'content' && (
                <ContentTooltip ref={setTooltipRef} {...getTooltipProps()} backgroundClass={props.backgroundClass} role={props.tooltipRole} visible={visible}>
                    {props.tooltipContent}
                </ContentTooltip>
            )}
        </Fragment>
    );

    return (
        <Fragment>
            {React.cloneElement(children, { ref: triggerRef ? composedTriggerRef : setTriggerRef })} {createPortal(component, parentEl || document.body)}
        </Fragment>
    );
}
